import { debug_log } from "../utils/debugLog";
import { asArray, getValue } from "../utils/tools";
import inflection from "inflection";
import intersection from "lodash/intersection";
import get from "lodash/get";
import { Store } from "../utils/store";

const actionToCommandMapping = {
  list: ["query", "read_all", "send"],
  show: "read",
  create: "create",
  edit: "update",
  delete: "delete",
  "bulk-create": "create_bulk",
};

const resourceAlias = {
  clients: "service_provider_client",
  webhooks: "service_provider_webhook",
};

const commandToAction = (command) =>
  Object.keys(actionToCommandMapping).find((action) =>
    asArray(actionToCommandMapping[action]).includes(command)
  );

const actionToCommands = (action) => {
  return asArray(actionToCommandMapping[action] || action).map((command) =>
    command.replaceAll("-", "_")
  );
};

const resourceToFileName = (resource) =>
  inflection
    .singularize(resourceAlias[resource] || resource)
    .replaceAll("-", "_");

const checkNoEmpty = (val, label, reqLabel) => {
  if (!val) {
    val = "";
    console.warn(`AccessControlProvider :: empty '${label}' on '${reqLabel}'`);
  }

  return val;
};

const domain = "backend";

const accessControlProvider = ({
  permissions = [],
  roles = [],
  actions = {},
  ...info
}) => ({
  permissions,
  roles,
  actions,
  info,

  getDomainActions: function () {
    return this.actions[domain];
  },

  getDomainResources: function () {
    return Object.keys(this.getDomainActions());
  },

  isValidResource: function (resource) {
    const resources = this.getDomainResources();
    return resources.includes(resourceToFileName(resource));
  },

  getCommands: function () {
    const result = {};

    for (const [_domain, domainSetup] of Object.entries(this.actions)) {
      result[_domain] = {};
      for (const [resource, resourceSetup] of Object.entries(domainSetup)) {
        result[_domain][resource] = Object.keys(resourceSetup);
      }
    }

    return result;
  },

  _getResourceCommands: function (resource) {
    resource = resourceToFileName(resource);
    return Object.keys(get(this.getDomainActions(), resource, {}));
  },

  _can: function (req, reqLabel) {
    if (req === true) {
      return true;
    }

    req = req || {};
    let { resource, action } = req;
    resource = checkNoEmpty(resource, "resource", reqLabel);
    action = checkNoEmpty(action, "action", reqLabel);

    const resourceCommands = this._getResourceCommands(resource);
    const commands = actionToCommands(action);

    let res = intersection(resourceCommands, commands).length;

    const filters = asArray(req?.filters).filter((v) => v);
    const allowedFilters = this.allowedFields(resource, action, "filters");
    if (Array.isArray(allowedFilters) && filters.length) {
      res = res && filters.every((filter) => allowedFilters.includes(filter));
    }

    !res &&
      debug_log(
        `AccessControlProvider :: ${reqLabel} / {${resource}, ${action}${
          filters.length ? ", " + filters.join("-") : ""
        }} ==> ${res}`,
        4
      );

    return res;
  },

  can: function (reqList, reqLabel) {
    reqList = asArray(reqList);

    return (
      reqList.filter((req) => {
        if (getValue(req, "roles")) {
          return this.hasRoles(req.roles);
        }

        return this._can(req, reqLabel);
      }).length === reqList.length
    );
  },

  allowedActions: function ({ resource }) {
    resource = checkNoEmpty(resource, "resource");
    const resourceCommands = this._getResourceCommands(resource);
    const resourceActions = resourceCommands.map(
      (command) => commandToAction(command) || `${command}**`
    );

    return resourceActions;
  },

  allowedFields: function (resource, action, type) {
    resource = resourceToFileName(checkNoEmpty(resource, "resource"));
    const resourceActions = get(this.getDomainActions(), resource, {});

    const commands = actionToCommands(action);
    const fields = commands
      .map((command) => get(resourceActions, `${command}.${type}`))
      .filter((v) => v)[0];

    return fields;
  },

  isAllowedPath: function ({ path }) {
    path = checkNoEmpty(path, "path");

    path = path.split("/").filter((v) => v);
    path = {
      resource: path[0],
      action: path[1],
    };

    //assumes the custom path action will have the same name that the remote method
    return this._can(path);
  },

  hasRoles: function (can) {
    const unauthorizedMarker = "-";

    can = asArray(can);
    const requiredRoles = can.filter(
      (role) => !role.startsWith(unauthorizedMarker)
    );
    const unauthorizedRoles = can
      .filter((role) => role.startsWith(unauthorizedMarker))
      .map((role) => role.substring(unauthorizedMarker.length));

    const nonUnauthorized = !intersection(unauthorizedRoles, roles).length;
    const required =
      !requiredRoles.length || intersection(requiredRoles, roles).length;

    return Boolean(can.length && roles.length && nonUnauthorized && required);
  },

  isSuperuser: function () {
    return this.hasRoles("superuser");
  },
});

let AccessControlProviderInstance = null;

export const resetPermissions = () => {
  AccessControlProviderInstance = null;
  debug_log("AccessControlProvider :: reset");
};

const cacheStorageKey = "RaStore.permissions";

export const getPermissionsData = (dataProvider) => {
  try {
    return dataProvider.customCommand(
      `permissions/?domain_filter=${domain}`,
      () => null,
      "GET"
    );
  } catch (error) {
    console.error(`getPermissionsData: ${error.message}`);
    return Promise.reject(error.message);
  }
};

export const FactoryAccessControlProvider = (dataProvider) => {
  if (AccessControlProviderInstance) {
    return Promise.resolve(AccessControlProviderInstance);
  }

  const cachedPermissions = Store.get(cacheStorageKey);
  if (cachedPermissions) {
    debug_log("AccessControlProvider :: using cache");
    AccessControlProviderInstance = accessControlProvider(cachedPermissions);
    return Promise.resolve(AccessControlProviderInstance);
  }

  debug_log("AccessControlProvider :: create");
  return getPermissionsData(dataProvider).then((res) => {
    Store.set(cacheStorageKey, res);
    AccessControlProviderInstance = accessControlProvider(res);
    return AccessControlProviderInstance;
  });
};
